import React from "react";
import axios, { AxiosResponse } from "axios";
import { useQuery } from "@tanstack/react-query";
import {
  ComparisonEnvironment,
  ShadowTestResultResponse,
  Stage,
} from "../lusca-search-api/generated-src";
import { ShadowTestApiFactory } from "../lusca-search-api/LuscaSearchApiFactory";

interface UseShadowTestResultParams {
  stage: Stage;
  transactionId: string | undefined;
}

// A triggered test writes its result asynchronously; poll until a result newer
// than the one shown at trigger time arrives, or give up after the timeout.
// Most tests land within a few seconds, so poll fast first and back off: on a
// flat 10s interval the result could not appear before the first tick, which
// read as "nothing is polling" and sent people to the browser refresh button.
export const SHADOW_TEST_POLL_SCHEDULE_MS = [1_000, 1_000, 2_000, 3_000, 5_000];
export const SHADOW_TEST_POLL_MAX_INTERVAL_MS = 10_000;
export const SHADOW_TEST_POLL_TIMEOUT_MS = 10 * 60_000;

// Elapsed wait at which each schedule step stops applying, so the current step
// can be found by index.
const SHADOW_TEST_POLL_STEP_UNTIL_MS = SHADOW_TEST_POLL_SCHEDULE_MS.reduce<
  number[]
>((until, delay) => [...until, (until[until.length - 1] ?? 0) + delay], []);

// Derived from elapsed wait time rather than a mutable attempt counter, because
// React Query recomputes refetchInterval on every result update rather than
// once per tick — a counter incremented here would skip ahead of the schedule.
export const shadowTestPollDelayMs = (elapsedMs: number) => {
  const step = SHADOW_TEST_POLL_STEP_UNTIL_MS.findIndex(
    (until) => elapsedMs < until
  );
  return step === -1
    ? SHADOW_TEST_POLL_MAX_INTERVAL_MS
    : SHADOW_TEST_POLL_SCHEDULE_MS[step];
};

interface PendingResult {
  comparisonEnvironment: ComparisonEnvironment;
  previousTestTimestamp?: number;
  startedAt: number;
}

/** A run we stopped waiting for; the baseline lets a late result clear it. */
interface TimedOutResult {
  comparisonEnvironment: ComparisonEnvironment;
  previousTestTimestamp?: number;
}

const responseMessage = (data: unknown) => {
  if (!data || typeof data !== "object") {
    return undefined;
  }
  const body = data as { error?: unknown; message?: unknown };
  if (typeof body.message === "string") {
    return body.message;
  }
  return typeof body.error === "string" ? body.error : undefined;
};

const isMissingResult = (
  error: unknown,
  transactionId: string,
  comparisonEnvironment?: ComparisonEnvironment
) => {
  if (!axios.isAxiosError(error) || error.response?.status !== 400) {
    return false;
  }

  const expectedMessage =
    comparisonEnvironment === ComparisonEnvironment.Beta
      ? `No BETA shadow test result found for: ${transactionId}`
      : `No shadow test result found for: ${transactionId}`;
  return responseMessage(error.response.data) === expectedMessage;
};

export default function useShadowTestResult({
  stage,
  transactionId,
}: UseShadowTestResultParams) {
  const shadowTestApi = ShadowTestApiFactory();
  const [pendingResult, setPendingResult] =
    React.useState<PendingResult | null>(null);
  const [timedOut, setTimedOut] = React.useState<TimedOutResult | null>(null);

  React.useEffect(() => {
    setPendingResult(null);
    setTimedOut(null);
  }, [stage, transactionId]);

  const pollingEnvironment = pendingResult?.comparisonEnvironment;
  const pollingStartedAt = pendingResult?.startedAt;

  const resultQuery = (comparisonEnvironment?: ComparisonEnvironment) => ({
    queryKey: [
      "shadowTestResult",
      stage,
      transactionId,
      comparisonEnvironment ?? ComparisonEnvironment.Gamma,
    ],
    queryFn: async () => {
      if (!transactionId) {
        throw new Error("Transaction ID is required");
      }
      try {
        const { data }: AxiosResponse<ShadowTestResultResponse> =
          await shadowTestApi.getShadowTestResult(
            stage,
            transactionId,
            comparisonEnvironment
          );
        return data;
      } catch (error) {
        if (isMissingResult(error, transactionId, comparisonEnvironment)) {
          return null;
        }
        throw error;
      }
    },
    enabled: !!transactionId && !!stage,
    retry: false,
    // Normally off — the interval covers a pending run and nothing else needs a
    // focus fetch. The exception is after a timeout: polling has stopped, and the
    // row-level cell in ProjectList has no refresh control, so without this there
    // is no path left to discover a late result and the warning would stick for
    // the rest of the session. Bounded: one fetch per focus, only while timed out.
    refetchOnWindowFocus: timedOut !== null,
    // Triggering a test and switching to another tab while it runs is the normal
    // way to use this, and React Query skips interval fetches on a blurred tab by
    // default. With refetchOnWindowFocus off there would be no catch-up fetch on
    // return either, so the result only landed once the user came back and waited
    // out another tick. Only polls while a run is pending, so this stays bounded
    // by SHADOW_TEST_POLL_TIMEOUT_MS.
    refetchIntervalInBackground: true,
    refetchInterval:
      pollingEnvironment ===
        (comparisonEnvironment ?? ComparisonEnvironment.Gamma) &&
      pollingStartedAt !== undefined
        ? () => shadowTestPollDelayMs(Date.now() - pollingStartedAt)
        : (false as const),
  });

  const gammaQuery = useQuery<ShadowTestResultResponse | null, Error>(
    resultQuery()
  );
  const betaQuery = useQuery<ShadowTestResultResponse | null, Error>(
    resultQuery(ComparisonEnvironment.Beta)
  );

  const pendingData =
    pendingResult?.comparisonEnvironment === ComparisonEnvironment.Beta
      ? betaQuery.data
      : gammaQuery.data;
  React.useEffect(() => {
    if (!pendingResult || !pendingData) return;
    if (
      pendingResult.previousTestTimestamp === undefined ||
      pendingData.testTimestamp > pendingResult.previousTestTimestamp
    ) {
      setPendingResult(null);
    }
  }, [pendingData, pendingResult]);

  React.useEffect(() => {
    if (!pendingResult) return undefined;
    const timeout = setTimeout(() => {
      // Record the give-up rather than just dropping the pending flag: on its
      // own that is indistinguishable from success, so a stale result was left
      // looking like the one the run had just produced. Keep the baseline so a
      // late result can still clear it.
      setTimedOut({
        comparisonEnvironment: pendingResult.comparisonEnvironment,
        previousTestTimestamp: pendingResult.previousTestTimestamp,
      });
      setPendingResult(null);
    }, SHADOW_TEST_POLL_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [pendingResult]);

  const timedOutData =
    timedOut?.comparisonEnvironment === ComparisonEnvironment.Beta
      ? betaQuery.data
      : gammaQuery.data;
  React.useEffect(() => {
    if (!timedOut || !timedOutData) return;
    // A result that arrives after we stopped waiting is this run's real output,
    // so drop the warning. Polling is already off by this point, so the fetch
    // that surfaces it comes from the modal's Refresh, a remount, or the
    // focus refetch enabled above for exactly this case.
    if (
      timedOut.previousTestTimestamp === undefined ||
      timedOutData.testTimestamp > timedOut.previousTestTimestamp
    ) {
      setTimedOut(null);
    }
  }, [timedOut, timedOutData]);

  /**
   * Starts waiting for a newer result in `comparisonEnvironment`.
   *
   * GAMMA and BETA are mutually exclusive candidate replays (both compared
   * against prod), and pending state is a single slot, so callers must not start
   * one while another is pending — `awaitingEnvironment` is the signal to gate
   * the trigger on. Overwriting would silently strand the first run's poll.
   */
  const awaitNewResult = (comparisonEnvironment: ComparisonEnvironment) => {
    const current =
      comparisonEnvironment === ComparisonEnvironment.Beta
        ? betaQuery.data
        : gammaQuery.data;
    // Only this environment's warning is answered by this run; a timed-out
    // result in the other one still predates whatever is on screen for it.
    setTimedOut((previous) =>
      previous?.comparisonEnvironment === comparisonEnvironment
        ? null
        : previous
    );
    setPendingResult({
      comparisonEnvironment,
      previousTestTimestamp: current?.testTimestamp,
      startedAt: Date.now(),
    });
  };

  const refetch = async () => {
    await Promise.all([gammaQuery.refetch(), betaQuery.refetch()]);
  };

  return {
    gammaResult: gammaQuery.data,
    betaResult: betaQuery.data,
    gammaIsLoading: gammaQuery.isLoading,
    betaIsLoading: betaQuery.isLoading,
    gammaIsFetching: gammaQuery.isFetching,
    betaIsFetching: betaQuery.isFetching,
    gammaError: gammaQuery.error || null,
    betaError: betaQuery.error || null,
    refetch,
    awaitNewResult,
    awaitingEnvironment: pendingResult?.comparisonEnvironment,
    /** Set when a triggered run produced no newer result before the timeout. */
    timedOutEnvironment: timedOut?.comparisonEnvironment,
  };
}
